import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';

import type { CreditNotesResponse } from './endpoints/CreditNotesEndpoint.types';
import type { CreditNoteDetail } from './endpoints/CreditNoteEndpoint.types';
import type { CreateCreditNoteBody, CreateCreditNoteResponse } from './endpoints/CreateCreditNoteEndpoint.types';
import type { ApproveCreditNoteResponse } from './endpoints/ApproveCreditNoteEndpoint.types';
import type { IPdfJobRef } from 'src/queue/pdf-job-queues';
import type { PeppolDelivery, SendPeppolResponse } from '../peppol/peppol.types';
import type { DeleteCreditNoteResponse } from './endpoints/DeleteCreditNoteEndpoint.types';
import type { ExportCreditNotesResponse } from './endpoints/ExportCreditNotesEndpoint.types';

export const CREDIT_NOTE_SWR_KEY = 'credit-note';

export interface CreditNotesFilters {
  search?: string;
}

export async function getCreditNotes(
  cursor?: string | null,
  take?: number,
  filters?: CreditNotesFilters,
  sortBy?: string,
  sortOrder?: string,
): Promise<CreditNotesResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('id', cursor);
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);

  const queryString = params.toString();
  const url = queryString ? `/api/v1/credit-notes?${queryString}` : '/api/v1/credit-notes';
  return fetchEndpointData<CreditNotesResponse>(url);
}

export async function getCreditNote(id: string): Promise<CreditNoteDetail> {
  return fetchEndpointData<CreditNoteDetail>(`/api/v1/credit-notes/${id}`);
}

// The GraphQL CreateCreditNoteInputData carried `date` as a Date scalar; over
// REST the client sends the same YYYY-MM-DD string it produced before and the
// server coerces it to a Date.
export type CreateCreditNoteInput = Omit<CreateCreditNoteBody, 'date'> & { date: string };

export async function createCreditNote(data: CreateCreditNoteInput): Promise<CreateCreditNoteResponse> {
  return fetchEndpointData<CreateCreditNoteResponse>('/api/v1/credit-notes', {
    method: 'POST',
    body: data,
  });
}

export async function sendCreditNote(
  creditNoteId: string,
  options?: { contactIds?: number[]; sendEmail?: boolean; sendPeppol?: boolean },
): Promise<IPdfJobRef> {
  return fetchEndpointData<IPdfJobRef>(`/api/v1/credit-notes/${creditNoteId}/send`, {
    method: 'POST',
    body: options ?? {},
  });
}

/** POST /v1/credit-notes/:id/send-peppol — manual push to Billit/Peppol. */
export async function sendCreditNoteViaPeppol(creditNoteId: string): Promise<SendPeppolResponse> {
  return fetchEndpointData<SendPeppolResponse>(`/api/v1/credit-notes/${creditNoteId}/send-peppol`, {
    method: 'POST',
  });
}

/** POST /v1/credit-notes/:id/refresh-peppol-status — fetch the delivery status from Billit now. */
export async function refreshCreditNotePeppolStatus(creditNoteId: string): Promise<PeppolDelivery> {
  return fetchEndpointData<PeppolDelivery>(`/api/v1/credit-notes/${creditNoteId}/refresh-peppol-status`, {
    method: 'POST',
  });
}

export async function generateCreditNote(creditNoteId: string): Promise<IPdfJobRef> {
  // Returns the enqueued PDF job reference ({ queue, jobId }) that the client
  // polls via runPdfJobAndRefresh — matches the GraphQL PdfGenerationJob result.
  return fetchEndpointData<IPdfJobRef>(`/api/v1/credit-notes/${creditNoteId}/generate`, {
    method: 'POST',
  });
}

export async function approveCreditNote(
  creditNoteId: string,
  creditNoteNumber?: number,
): Promise<ApproveCreditNoteResponse> {
  return fetchEndpointData<ApproveCreditNoteResponse>(`/api/v1/credit-notes/${creditNoteId}/approve`, {
    method: 'POST',
    body: { creditNoteNumber },
  });
}

export async function deleteCreditNote(
  creditNoteId: string,
  opts?: { confirm?: boolean },
): Promise<DeleteCreditNoteResponse> {
  return fetchEndpointData<DeleteCreditNoteResponse>(`/api/v1/credit-notes/${creditNoteId}`, {
    method: 'DELETE',
    body: opts?.confirm ? { confirm: true } : undefined,
  });
}

export async function exportCreditNotes(body: {
  startId: string;
  endId: string;
  financialPeriod: number;
}): Promise<ExportCreditNotesResponse> {
  return fetchEndpointData<ExportCreditNotesResponse>('/api/v1/credit-notes/export', {
    method: 'POST',
    body,
  });
}

export function useCreditNote(id: string) {
  return useSWR([CREDIT_NOTE_SWR_KEY, id], () => getCreditNote(id));
}
